import { Modal } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { AiOutlineClose, AiOutlineDownload, AiOutlinePrinter } from 'react-icons/ai'
import * as styles from './PdfViewer.css'

// Shows a PDF (Confirmation of Purchase, deal report, etc.) inline in a modal with a
// Download / Print / Close toolbar. The document is fetched and rendered from a same-origin
// blob URL — a direct cross-origin S3 URL is refused by the browser in an <iframe> (its
// `Content-Disposition: attachment` / X-Frame-Options makes it "content is blocked"), even
// though the URL itself is valid (Download works). The blob also lets Print work. If the
// fetch is blocked (CORS), fall back to Download.
export const PdfViewer = ({ url, title = 'Document', open, onClose }) => {
  const frameRef = useRef(null)
  const [blobUrl, setBlobUrl] = useState(null)
  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'error'

  useEffect(() => {
    if (!open || !url) return undefined
    let cancelled = false
    let created = null
    setStatus('loading')
    setBlobUrl(null)
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.blob()
      })
      .then(blob => {
        if (cancelled) return
        const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
        created = URL.createObjectURL(pdfBlob)
        setBlobUrl(created)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [open, url])

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = blobUrl || url
    link.download = `${title || 'document'}.pdf`
    if (!blobUrl) {
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
    }
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const handlePrint = () => {
    try {
      frameRef.current?.contentWindow?.focus()
      frameRef.current?.contentWindow?.print()
    } catch (err) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Modal
      open={!!open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={1000}
      bodyStyle={{ padding: 0, overflow: 'hidden', borderRadius: 12 }}>
      <div className={styles.viewer}>
        <div className={styles.toolbar}>
          <span className={styles.title}>{title}</span>
          <div className={styles.actions}>
            <button className={styles.action} onClick={handleDownload} type='button'>
              <AiOutlineDownload /> Download
            </button>
            <button className={styles.action} onClick={handlePrint} type='button' disabled={status !== 'ready'}>
              <AiOutlinePrinter /> Print
            </button>
            <button className={styles.close} onClick={onClose} type='button'>
              <AiOutlineClose /> Close
            </button>
          </div>
        </div>
        {status === 'ready' ? (
          <iframe ref={frameRef} src={blobUrl} title={title} className={styles.frame} />
        ) : status === 'error' ? (
          <div className={styles.fallback}>
            <p className={styles.fallbackText}>Preview couldn&apos;t load in the browser.</p>
            <button className={styles.action} onClick={handleDownload} type='button'>
              <AiOutlineDownload /> Download instead
            </button>
          </div>
        ) : (
          <div className={styles.fallback}>
            <p className={styles.fallbackText}>Loading document…</p>
          </div>
        )}
      </div>
    </Modal>
  )
}
